<template>
  <div class="odontogram-container">
    <!-- Cabeçalho com informações do paciente -->
    <div class="odontogram-header">
      <div class="patient-info">
        <h4 class="mb-0">Odontograma Digital</h4>
        <small class="text-muted">
          {{ formatHyphenToSlash(odontograma.data) || "Não informado" }} •
          {{
            (odontograma.paciente && odontograma.paciente.nome) ||
            "Não informado"
          }}
        </small>
      </div>
      <div class="quick-actions">
        <b-button variant="outline-primary" size="sm" @click="clearAll">
          🔄 Limpar Tudo
        </b-button>
        <b-button variant="success" size="sm" @click="saveChanges">
          💾 Salvar
        </b-button>
      </div>
    </div>

    <div class="odontogram-content">
      <!-- Odontograma Principal -->
      <div class="dental-chart">
        <!-- Arcada Superior -->
        <div class="arch upper-arch">
          <div class="arch-label">Arcada Superior</div>
          <div class="teeth-row">
            <div
              v-for="tooth in upperTeeth"
              :key="tooth.id"
              class="tooth-wrapper"
            >
              <div
                class="tooth"
                :class="getToothClasses(tooth)"
                @click="selectTooth(tooth)"
              >
                <div class="tooth-number">{{ tooth.numero }}</div>
                <div class="tooth-faces">
                  <div
                    class="face oclusal"
                    :class="getFaceClasses('oclusal', tooth.faces.oclusal)"
                    :style="getFaceStyle(tooth.faces.oclusal, 'oclusal')"
                    @click.stop="editFace(tooth, 'oclusal')"
                  />
                  <div
                    class="face vestibular"
                    :class="getFaceClasses(tooth.faces.vestibular)"
                    :style="getFaceStyle(tooth.faces.vestibular, 'vestibular')"
                    @click.stop="editFace(tooth, 'vestibular')"
                  />
                  <div
                    class="face lingual"
                    :class="getFaceClasses(tooth.faces.lingual)"
                    :style="getFaceStyle(tooth.faces.lingual, 'lingual')"
                    @click.stop="editFace(tooth, 'lingual')"
                  />
                  <div
                    class="face mesial"
                    :class="getFaceClasses(tooth.faces.mesial)"
                    :style="getFaceStyle(tooth.faces.mesial, 'mesial')"
                    @click.stop="editFace(tooth, 'mesial')"
                  />
                  <div
                    class="face distal"
                    :class="getFaceClasses(tooth.faces.distal)"
                    :style="getFaceStyle(tooth.faces.distal, 'distal')"
                    @click.stop="editFace(tooth, 'distal')"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Arcada Inferior -->
        <div class="arch lower-arch">
          <div class="arch-label">Arcada Inferior</div>
          <div class="teeth-row">
            <div
              v-for="tooth in lowerTeeth"
              :key="tooth.id"
              class="tooth-wrapper"
            >
              <div
                class="tooth"
                :class="getToothClasses(tooth)"
                @click="selectTooth(tooth)"
              >
                <div class="tooth-number">{{ tooth.numero }}</div>
                <div class="tooth-faces">
                  <div
                    class="face oclusal"
                    :class="getFaceClasses(tooth.faces.oclusal)"
                    :style="getFaceStyle(tooth.faces.oclusal, 'oclusal')"
                    @click.stop="editFace(tooth, 'oclusal')"
                  />
                  <div
                    class="face vestibular"
                    :class="getFaceClasses(tooth.faces.vestibular)"
                    :style="getFaceStyle(tooth.faces.vestibular, 'vestibular')"
                    @click.stop="editFace(tooth, 'vestibular')"
                  />
                  <div
                    class="face lingual"
                    :class="getFaceClasses(tooth.faces.lingual)"
                    :style="getFaceStyle(tooth.faces.lingual, 'lingual')"
                    @click.stop="editFace(tooth, 'lingual')"
                  />
                  <div
                    class="face mesial"
                    :class="getFaceClasses(tooth.faces.mesial)"
                    :style="getFaceStyle(tooth.faces.mesial, 'mesial')"
                    @click.stop="editFace(tooth, 'mesial')"
                  />
                  <div
                    class="face distal"
                    :class="getFaceClasses(tooth.faces.distal)"
                    :style="getFaceStyle(tooth.faces.distal, 'distal')"
                    @click.stop="editFace(tooth, 'distal')"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Painel Lateral -->
      <div class="side-panel">
        <!-- Detalhes do Dente Selecionado -->
        <div class="tooth-details" v-if="selectedTooth">
          <div class="detail-header">
            <h5 class="mb-0">Dente #{{ selectedTooth.numero }}</h5>
            <b-badge variant="primary">Selecionado</b-badge>
          </div>

          <div class="faces-status">
            <div
              class="face-item"
              v-for="(face, key) in selectedTooth.faces"
              :key="key"
              :class="{ 'face-selected': selectedFace === key }"
              @click="selectedFace = key"
            >
              <div
                class="face-indicator"
                :class="{ active: face.ativo }"
                :style="getFaceStyle(face, key)"
              ></div>
              <span class="face-name">{{ getFaceName(key) }}</span>
              <span class="face-status">
                <span
                  v-if="face.ativo && face.corHex"
                  class="condition-indicator"
                  :style="{ backgroundColor: face.corHex }"
                ></span>
                {{ face.ativo ? "Marcado" : "Normal" }}
              </span>
            </div>
          </div>
        </div>

        <!-- Painel de Ações -->
        <div class="actions-panel">
          <h6>Ações Rápidas</h6>
          <div class="action-buttons">
            <b-button
              variant="outline-danger"
              size="sm"
              class="action-btn"
              @click="applyCondition('caries')"
            >
              <div class="color-preview caries"></div>
              Cárie
            </b-button>
            <b-button
              variant="outline-info"
              size="sm"
              class="action-btn"
              @click="applyCondition('obturacao')"
            >
              <div class="color-preview obturation"></div>
              Obturação
            </b-button>
            <b-button
              variant="outline-warning"
              size="sm"
              class="action-btn"
              @click="applyCondition('tratamento')"
            >
              <div class="color-preview treatment"></div>
              Tratamento
            </b-button>
            <b-button
              variant="outline-secondary"
              size="sm"
              class="action-btn"
              @click="applyCondition('ausente')"
            >
              <div class="color-preview absent"></div>
              Ausente
            </b-button>
            <b-button
              variant="outline-success"
              size="sm"
              class="action-btn"
              @click="applyCondition('implante')"
            >
              <div class="color-preview implant"></div>
              Implante
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Edição do Dente -->
    <b-modal
      v-model="showToothEditModal"
      :title="
        'Editar Dente #' +
        (selectedToothForModal && selectedToothForModal.numero)
      "
      size="lg"
      centered
      hide-footer
      no-close-on-backdrop
    >
      <div v-if="selectedToothForModal" class="tooth-edit-modal">
        <!-- Visualização do Dente -->
        <div class="tooth-visualization">
          <div class="tooth-preview">
            <div class="tooth-number">{{ selectedToothForModal.numero }}</div>
            <div class="tooth-faces">
              <div
                v-for="(face, faceKey) in selectedToothForModal.faces"
                :key="faceKey"
                class="face"
                :class="{ active: face.ativo }"
                :style="getFaceStyle(face, faceKey)"
                @click="editFaceInModal(faceKey)"
              >
                <span class="face-label">{{
                  getFaceName(faceKey).charAt(0)
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Painel de Condições -->
        <div class="conditions-panel">
          <h6>Condições Disponíveis</h6>
          <div class="condition-options">
            <b-button
              v-for="(color, condition) in conditionColors"
              :key="condition"
              variant="outline-secondary"
              size="sm"
              class="condition-btn"
              :class="{ active: selectedCondition === condition }"
              @click="selectCondition(condition)"
            >
              <div
                class="color-preview"
                :style="{ backgroundColor: color }"
              ></div>
              {{ getConditionName(condition) }}
            </b-button>
          </div>
        </div>

        <!-- Status das Faces -->
        <div class="faces-status-panel">
          <h6>Status das Faces</h6>
          <div class="faces-list">
            <div
              v-for="(face, faceKey) in selectedToothForModal.faces"
              :key="faceKey"
              class="face-status-item"
              :class="{ 'face-selected': selectedFaceInModal === faceKey }"
              @click="selectFaceInModal(faceKey)"
            >
              <div class="face-info">
                <span class="face-name">{{ getFaceName(faceKey) }}</span>
                <span class="face-condition">
                  <span
                    v-if="face.ativo && face.corHex"
                    class="condition-indicator"
                    :style="{ backgroundColor: face.corHex }"
                  ></span>
                  {{
                    face.ativo ? getConditionNameByColor(face.corHex) : "Normal"
                  }}
                </span>
              </div>
              <b-button
                v-if="face.ativo"
                variant="outline-danger"
                size="sm"
                @click.stop="clearFace(faceKey)"
              >
                X
              </b-button>
            </div>
          </div>
        </div>

        <!-- Ações do Modal -->
        <div class="modal-actions">
          <b-button variant="secondary" @click="closeToothModal">
            Cancelar
          </b-button>
          <b-button variant="primary" @click="saveToothChanges">
            Salvar Alterações
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { formatHyphenToSlash } from "@/utils/dateFormatter";
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BFormGroup,
  BFormCheckbox,
  BFormTextarea,
  BModal,
  BBadge,
} from "bootstrap-vue";

export default {
  name: "DigitalOdontogram",
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormCheckbox,
    BFormTextarea,
    BModal,
    BBadge,
  },
  props: {
    odontograma: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectedTooth: null,
      editedTooth: null,
      selectedFace: null,
      showModal: false,
      showToothEditModal: false,
      selectedToothForModal: null,
      selectedFaceInModal: null,
      selectedCondition: null,
      conditionColors: {
        normal: "#ffffff",
        caries: "#ff6b6b",
        obturation: "#4ecdc4",
        treatment: "#45b7d1",
        absent: "#6c757d",
        implant: "#ffd93d",
      },
    };
  },
  computed: {
    upperTeeth() {
      return this.odontograma.dentes
        .filter((t) => {
          const info = this.getFdiInfo(t.numero);
          return info && info.arcada === "upper";
        })
        .sort((a, b) => this.sortByFdi(a.numero, b.numero, "upper"));
    },

    lowerTeeth() {
      return this.odontograma.dentes
        .filter((t) => {
          const info = this.getFdiInfo(t.numero);
          return info && info.arcada === "lower";
        })
        .sort((a, b) => this.sortByFdi(a.numero, b.numero, "lower"));
    },
  },

  methods: {
    sortByFdi(a, b, arcada) {
      const na = parseInt(a, 10);
      const nb = parseInt(b, 10);

      if (arcada === "upper") {
        // Superior: direita → esquerda
        return nb - na;
      }

      // Inferior: esquerda → direita
      return na - nb;
    },
    getFdiInfo(numero) {
      const n = parseInt(numero, 10);
      if (isNaN(n)) return null;

      const quadrant = Math.floor(n / 10);

      const map = {
        1: { arcada: "upper", tipo: "permanente" },
        2: { arcada: "upper", tipo: "permanente" },
        3: { arcada: "lower", tipo: "permanente" },
        4: { arcada: "lower", tipo: "permanente" },
        5: { arcada: "upper", tipo: "deciduo" },
        6: { arcada: "upper", tipo: "deciduo" },
        7: { arcada: "lower", tipo: "deciduo" },
        8: { arcada: "lower", tipo: "deciduo" },
      };

      return map[quadrant] || null;
    },
    commitEditedTooth() {
      if (!this.editedTooth || !this.editedTooth.id) return;

      const index = this.odontograma.dentes.findIndex(
        (t) => t.id === this.editedTooth.id,
      );

      if (index !== -1) {
        // Vue 2 precisa de $set para manter reatividade
        this.$set(
          this.odontograma.dentes,
          index,
          JSON.parse(JSON.stringify(this.editedTooth)),
        );
      }
    },
    formatHyphenToSlash(dataString) {
      return formatHyphenToSlash(dataString);
    },

    selectTooth(tooth) {
      this.selectedTooth = JSON.parse(JSON.stringify(tooth));
      this.editedTooth = JSON.parse(JSON.stringify(tooth));
      // Abrir modal de edição
      this.openToothModal(tooth);
    },

    editFace(tooth, faceKey) {
      this.selectTooth(tooth);
      this.selectedFace = faceKey;
      // Toggle do estado da face
      if (this.editedTooth.faces[faceKey]) {
        this.editedTooth.faces[faceKey].ativo =
          !this.editedTooth.faces[faceKey].ativo;
        // Se desativou, remove a cor
        if (!this.editedTooth.faces[faceKey].ativo) {
          this.editedTooth.faces[faceKey].corHex = null;
        }
      }
    },

    getToothClasses(tooth) {
      return {
        "tooth-selected":
          this.selectedTooth && this.selectedTooth.id === tooth.id,
        "tooth-modified": this.isToothModified(tooth),
      };
    },

    getFaceClasses(faceKey, face) {
      if (!face) {
        return {};
      }

      return {
        active: !!face.ativo,
        "face-selected": this.selectedFace === faceKey,
      };
    },

    isToothModified(tooth) {
      return Object.values(tooth.faces).some((face) => face.ativo);
    },

    getFaceName(faceKey) {
      const faces = {
        oclusal: "Oclusal",
        vestibular: "Vestibular",
        lingual: "Lingual",
        mesial: "Mesial",
        distal: "Distal",
      };
      return faces[faceKey] || faceKey;
    },

    getFaceStyle(face, faceKey) {
      if (!face || !face.ativo) {
        return {
          backgroundColor: "#ffffff",
          borderColor: "#bdc3c7",
        };
      }

      return {
        backgroundColor: face.corHex || "#ffffff",
        borderColor: face.corHex || "#bdc3c7",
        borderWidth: face.corHex ? "2px" : "1px",
      };
    },

    applyCondition(condition) {
      if (!this.selectedTooth) return;

      const color = this.conditionColors[condition];

      if (this.selectedFace) {
        const face = this.editedTooth.faces[this.selectedFace];
        face.ativo = true;
        face.corHex = color;
      } else {
        Object.values(this.editedTooth.faces).forEach((face) => {
          face.ativo = true;
          face.corHex = color;
        });
      }

      this.commitEditedTooth();
    },

    clearAll() {
      // Limpa todas as marcações
      this.odontograma.dentes.forEach((tooth) => {
        Object.keys(tooth.faces).forEach((faceKey) => {
          tooth.faces[faceKey].ativo = false;
          tooth.faces[faceKey].corHex = null;
        });
      });

      this.selectedTooth = null;
      this.editedTooth = null;

      this.$bvToast.toast("Odontograma limpo", {
        title: "Sucesso",
        variant: "info",
        solid: true,
      });
    },

    saveChanges() {
      this.$emit("tooth-updated", this.editedTooth);
      this.$bvToast.toast("Alterações salvas com sucesso", {
        title: "Sucesso",
        variant: "success",
        solid: true,
      });
    },

    // Métodos do Modal de Edição
    openToothModal(tooth) {
      this.selectedToothForModal = JSON.parse(JSON.stringify(tooth));
      this.selectedFaceInModal = null;
      this.selectedCondition = null;
      this.showToothEditModal = true;
    },

    closeToothModal() {
      this.showToothEditModal = false;
      this.selectedToothForModal = null;
      this.selectedFaceInModal = null;
      this.selectedCondition = null;
    },

    selectFaceInModal(faceKey) {
      this.selectedFaceInModal = faceKey;
    },

    selectCondition(condition) {
      this.selectedCondition = condition;
      // Se houver uma face selecionada, aplicar a condição imediatamente
      if (this.selectedFaceInModal) {
        this.applyConditionToFace(this.selectedFaceInModal, condition);
      }
    },

    applyConditionToFace(faceKey, condition) {
      if (
        !this.selectedToothForModal ||
        !this.selectedToothForModal.faces[faceKey]
      ) {
        return;
      }

      const face = this.selectedToothForModal.faces[faceKey];

      if (condition === "normal") {
        face.ativo = false;
        face.corHex = null;
      } else {
        face.ativo = true;
        face.corHex = this.conditionColors[condition];
      }
    },

    editFaceInModal(faceKey) {
      this.selectFaceInModal(faceKey);
      // Se houver uma condição selecionada, aplicá-la à face
      if (this.selectedCondition) {
        this.applyConditionToFace(faceKey, this.selectedCondition);
      }
    },

    clearFace(faceKey) {
      if (
        this.selectedToothForModal &&
        this.selectedToothForModal.faces[faceKey]
      ) {
        this.selectedToothForModal.faces[faceKey].ativo = false;
        this.selectedToothForModal.faces[faceKey].corHex = null;
      }
    },

    getConditionName(condition) {
      const names = {
        normal: "Normal",
        caries: "Cárie",
        obturation: "Obturação",
        treatment: "Tratamento",
        absent: "Ausente",
        implant: "Implante",
      };
      return names[condition] || condition;
    },

    getConditionNameByColor(colorHex) {
      const condition = Object.keys(this.conditionColors).find(
        (key) => this.conditionColors[key] === colorHex,
      );
      return condition ? this.getConditionName(condition) : "Desconhecido";
    },

    async saveToothChanges() {
      try {
        // Preparar dados para envio à API
        const toothData = {
          id: this.selectedToothForModal.id,
          numero: this.selectedToothForModal.numero,
          faces: this.selectedToothForModal.faces,
        };

        // Enviar para a API
        const response = await this.$http.put(
          `/atendimento/odontograma/dente/${this.selectedToothForModal.id}`,
          toothData,
        );

        // Atualizar o dente no odontograma principal
        const index = this.odontograma.dentes.findIndex(
          (t) => t.id === this.selectedToothForModal.id,
        );

        if (index !== -1) {
          this.$set(
            this.odontograma.dentes,
            index,
            JSON.parse(JSON.stringify(this.selectedToothForModal)),
          );
        }

        // Atualizar selectedTooth e editedTooth também
        this.selectedTooth = JSON.parse(
          JSON.stringify(this.selectedToothForModal),
        );
        this.editedTooth = JSON.parse(
          JSON.stringify(this.selectedToothForModal),
        );

        this.$bvToast.toast("Dente atualizado com sucesso!", {
          title: "Sucesso",
          variant: "success",
          solid: true,
        });

        this.closeToothModal();
      } catch (error) {
        console.error("Erro ao salvar dente:", error);
        this.$bvToast.toast("Erro ao salvar as alterações do dente", {
          title: "Erro",
          variant: "danger",
          solid: true,
        });
      }
    },
  },
};
</script>
<style scoped>
/* Container Principal */
.odontogram-container {
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  max-width: 100%;
  overflow-x: auto;
  padding: 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

/* Cabeçalho */
.odontogram-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.patient-info h4 {
  color: #2c3e50;
  font-weight: 600;
}

.quick-actions {
  display: flex;
  gap: 10px;
}

/* Conteúdo Principal */
.odontogram-content {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 20px;
  align-items: start;
}

/* Odontograma */
.dental-chart {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 600px;
}

/* Arcadas */
.arch {
  background: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.arch:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.upper-arch {
  border-bottom: 4px solid #3498db;
}

.lower-arch {
  border-top: 4px solid #3498db;
}

.arch-label {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 1.1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  text-align: center;
}

/* Fileira de Dentes */
.teeth-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

/* Dente Individual */
.tooth-wrapper {
  position: relative;
  width: 70px;
  height: 120px;
  margin: 0 2px;
}

.tooth {
  width: 100%;
  height: 100%;
  border: 2px solid #95a5a6;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  background: linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  padding-top: 10px;
}

.tooth:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  border-color: #3498db;
}

.tooth-selected {
  border: 3px solid #3498db;
  box-shadow: 0 0 0 0.4rem rgba(52, 152, 219, 0.25);
  background: linear-gradient(145deg, #e3f2fd 0%, #bbdefb 100%);
  transform: translateY(-2px);
}

.tooth-modified {
  border-color: #e74c3c;
  background: linear-gradient(145deg, #fff5f5 0%, #ffe0e0 100%);
}

.tooth-number {
  font-weight: bold;
  font-size: 1.1rem;
  margin-bottom: 10px;
  color: #2c3e50;
  background: #ecf0f1;
  padding: 4px 8px;
  border-radius: 6px;
  min-width: 32px;
  text-align: center;
  transition: all 0.3s ease;
}

.tooth-selected .tooth-number {
  background: #3498db;
  color: white;
}

/* Faces do Dente */
.tooth-faces {
  display: grid;
  grid-template-areas:
    ". oclusal ."
    "mesial vestibular distal"
    ". lingual .";
  grid-gap: 4px;
  width: 85%;
  height: 70%;
  flex-grow: 1;
}

.face {
  background-color: #ffffff;
  border: 1px solid #bdc3c7;
  border-radius: 4px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
}

.face:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 10;
}

.face.active {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  border-color: #2c3e50;
  border-width: 2px;
}

.face.face-selected {
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.5);
  z-index: 20;
}

/* Posicionamento das Faces */
.oclusal {
  grid-area: oclusal;
}
.vestibular {
  grid-area: vestibular;
}
.lingual {
  grid-area: lingual;
}
.mesial {
  grid-area: mesial;
}
.distal {
  grid-area: distal;
}

/* Indicador de Face Selecionada */
.selected-face-indicator {
  margin-bottom: 15px;
  padding: 10px;
  background: #e3f2fd;
  border: 1px solid #3498db;
  border-radius: 6px;
}

.selected-face-info {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 5px;
}

.selected-face-info .face-indicator {
  width: 14px;
  height: 14px;
}

.selected-face-info .face-name {
  font-weight: 600;
  color: #2c3e50;
  flex-grow: 1;
}

/* Indicador de Condição no Painel */
.condition-indicator {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 6px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

/* Painel Lateral */
.side-panel {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.tooth-details {
  background: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #ecf0f1;
}

.faces-status {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.face-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border-radius: 6px;
  transition: background 0.3s ease;
}

.face-item:hover {
  background: #f8f9fa;
}

.face-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ecf0f1;
  border: 2px solid #bdc3c7;
  transition: all 0.3s ease;
}

.face-indicator.active {
  background: #27ae60;
  border-color: #229954;
  box-shadow: 0 0 0 3px rgba(39, 174, 96, 0.2);
}

.face-name {
  font-weight: 500;
  color: #2c3e50;
  min-width: 80px;
}

.face-status {
  margin-left: auto;
  font-size: 0.9rem;
  color: #7f8c8d;
}

/* Painel de Ações */
.actions-panel {
  background: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.actions-panel h6 {
  margin-bottom: 15px;
  color: #2c3e50;
  font-weight: 600;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: flex-start;
  padding: 10px 15px;
  transition: all 0.3s ease;
}

.action-btn:hover {
  transform: translateX(4px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.color-preview {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #ecf0f1;
  flex-shrink: 0;
}

.color-preview.caries {
  background: #ff6b6b;
}
.color-preview.obturation {
  background: #4ecdc4;
}
.color-preview.treatment {
  background: #45b7d1;
}
.color-preview.absent {
  background: #6c757d;
}
.color-preview.implant {
  background: #ffd93d;
}

/* Responsividade */
@media (max-width: 1200px) {
  .odontogram-content {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .side-panel {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }

  .dental-chart {
    min-width: 500px;
  }

  .tooth-wrapper {
    width: 60px;
    height: 100px;
  }
}

@media (max-width: 768px) {
  .odontogram-container {
    padding: 15px;
  }

  .odontogram-header {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }

  .dental-chart {
    min-width: 100%;
    gap: 15px;
  }

  .arch {
    padding: 15px;
  }

  .tooth-wrapper {
    width: 50px;
    height: 85px;
  }

  .tooth-number {
    font-size: 0.9rem;
    padding: 2px 6px;
    min-width: 28px;
  }

  .tooth-faces {
    grid-gap: 3px;
    width: 80%;
    height: 65%;
  }

  .side-panel {
    grid-template-columns: 1fr;
  }

  .arch-label {
    font-size: 1rem;
    margin-bottom: 10px;
  }

  .teeth-row {
    gap: 8px;
  }
}

@media (max-width: 480px) {
  .tooth-wrapper {
    width: 45px;
    height: 75px;
  }

  .tooth-number {
    font-size: 0.8rem;
  }

  .teeth-row {
    gap: 6px;
  }

  .action-btn {
    padding: 8px 12px;
    font-size: 0.9rem;
  }
}

/* Animações Suaves */
@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

.tooth-selected {
  animation: pulse 2s infinite;
}

/* Estados de Foco para Acessibilidade */
.tooth:focus,
.face:focus {
  outline: 3px solid #3498db;
  outline-offset: 2px;
}

/* Tooltip de Informação (opcional) */
.tooth::before {
  content: attr(data-tooltip);
  position: absolute;
  bottom: -30px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
  z-index: 1000;
}

.tooth:hover::before {
  opacity: 1;
}

/* Estilos do Modal de Edição */
.tooth-edit-modal {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 10px 0;
}

.tooth-visualization {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 10px;
  border: 2px solid #e9ecef;
}

.tooth-preview {
  position: relative;
  width: 120px;
  height: 180px;
  border: 3px solid #3498db;
  border-radius: 15px;
  background: linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 15px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.tooth-preview .tooth-number {
  font-weight: bold;
  font-size: 1.4rem;
  margin-bottom: 15px;
  color: #2c3e50;
  background: #3498db;
  color: white;
  padding: 6px 12px;
  border-radius: 8px;
  min-width: 40px;
  text-align: center;
}

.tooth-preview .tooth-faces {
  display: grid;
  grid-template-areas:
    ". oclusal ."
    "mesial vestibular distal"
    ". lingual .";
  grid-gap: 6px;
  width: 90%;
  height: 70%;
  flex-grow: 1;
}

.tooth-preview .face {
  background-color: #ffffff;
  border: 2px solid #bdc3c7;
  border-radius: 6px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.8rem;
  color: #2c3e50;
}

.tooth-preview .face:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 10;
}

.tooth-preview .face.active {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  border-color: #2c3e50;
  border-width: 3px;
  color: white;
  font-weight: bold;
}

.tooth-preview .face .face-label {
  text-transform: uppercase;
  font-size: 0.7rem;
  font-weight: bold;
}

.conditions-panel {
  background: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.conditions-panel h6 {
  margin-bottom: 15px;
  color: #2c3e50;
  font-weight: 600;
}

.condition-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
}

.condition-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-start;
  padding: 10px 15px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.condition-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.condition-btn.active {
  border-color: #3498db;
  background-color: #e3f2fd;
  color: #3498db;
}

.condition-btn .color-preview {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #ecf0f1;
  flex-shrink: 0;
}

.faces-status-panel {
  background: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.faces-status-panel h6 {
  margin-bottom: 15px;
  color: #2c3e50;
  font-weight: 600;
}

.faces-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.face-status-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 2px solid transparent;
}

.face-status-item:hover {
  background: #f8f9fa;
  border-color: #e9ecef;
}

.face-status-item.face-selected {
  background: #e3f2fd;
  border-color: #3498db;
}

.face-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-grow: 1;
}

.face-info .face-name {
  font-weight: 600;
  color: #2c3e50;
  min-width: 80px;
}

.face-info .face-condition {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: #7f8c8d;
}

.face-info .condition-indicator {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 15px;
  border-top: 2px solid #e9ecef;
}

.modal-actions .btn {
  min-width: 120px;
}

/* Responsividade do Modal */
@media (max-width: 768px) {
  .tooth-preview {
    width: 100px;
    height: 150px;
    padding: 10px;
  }

  .tooth-preview .tooth-number {
    font-size: 1.2rem;
    padding: 4px 8px;
  }

  .condition-options {
    grid-template-columns: repeat(2, 1fr);
  }

  .modal-actions {
    flex-direction: column;
  }

  .modal-actions .btn {
    width: 100%;
  }
}
</style>
